import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { submissionsService } from '../../services/submissions.service';
import LoadingState from '../../components/ui/LoadingState';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../lib/routes';

const { FiInbox, FiBook, FiUsers, FiMegaphone, FiGlobe, FiMapPin, FiArrowRight } = FiIcons;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await submissionsService.getAdminStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading admin stats:', err);
      setStats({
        pending_submissions: 0,
        total_resources: 0,
        active_ads: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState />;

  const adminModules = [
    {
      title: 'Moderation Queue',
      desc: 'Review and approve volunteer submissions.',
      icon: FiInbox,
      link: ROUTES.ADMIN.MODERATION,
      count: stats?.pending_submissions || 0,
      color: 'bg-amber-600',
    },
    {
      title: 'Resource Library',
      desc: 'Create and edit knowledge base content.',
      icon: FiBook,
      link: ROUTES.ADMIN.CONTENT,
      count: stats?.total_resources || 0,
      color: 'bg-blue-600',
    },
    {
      title: 'Ad Campaigns',
      desc: 'Manage sponsor ads and placements.',
      icon: FiMegaphone,
      link: ROUTES.ADMIN.ADS,
      count: stats?.active_ads || 0,
      color: 'bg-slate-900',
    },
    {
      title: 'User Management',
      desc: 'Manage permissions and roles.',
      icon: FiUsers,
      link: ROUTES.ADMIN.USERS,
      count: null,
      color: 'bg-green-600',
    },
    {
      title: 'Translations',
      desc: 'Manage multilingual content coverage.',
      icon: FiGlobe,
      link: ROUTES.ADMIN.TRANSLATIONS,
      count: null,
      color: 'bg-cyan-600',
    },
    {
      title: 'Directory',
      desc: 'Manage hospitals, organizations, and support services.',
      icon: FiMapPin,
      link: ROUTES.ADMIN.DIRECTORY,
      count: null,
      color: 'bg-rose-600',
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-12">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight">Admin Control Center</h1>
        <p className="text-slate-500 mt-2 font-medium">
          Monitor platform health and manage moderated content.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminModules.map((module, idx) => (
          <motion.div
            key={module.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08 }}
            className="group"
          >
            <Link
              to={module.link}
              className="block bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all h-full"
            >
              <div className={`w-14 h-14 ${module.color} rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg`}>
                <SafeIcon icon={module.icon} className="text-2xl" />
              </div>

              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-black text-slate-900">{module.title}</h3>
                {module.count !== null && (
                  <span className="text-xs font-black bg-slate-100 px-3 py-1 rounded-full text-slate-600">
                    {module.count}
                  </span>
                )}
              </div>

              <p className="text-slate-500 text-sm font-medium mb-6 leading-relaxed">{module.desc}</p>

              <div className="flex items-center text-blue-600 font-bold text-sm group-hover:translate-x-1 transition-transform">
                Open Module <SafeIcon icon={FiArrowRight} className="ml-2" />
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}